<template>
  <div>
    <v-card class="mb-3">
      <v-card-text>
        <v-row align="center">
          <v-col cols="12" sm="6" md="4">
            <v-text-field
              v-model="search"
              append-icon="mdi-magnify"
              label="Buscar producto"
              outlined dense hide-details
              @keyup.enter="loadProducts"
            ></v-text-field>
          </v-col>
          <v-col cols="12" sm="6" md="3" v-if="role === 'SUPERADMIN'">
            <v-select
              outlined dense hide-details
              :items="offices"
              item-text="name"
              item-value="id"
              label="Sede"
              clearable
              v-model="filterOffice"
            ></v-select>
          </v-col>
          <v-col cols="auto">
            <v-btn color="secondary" @click="loadProducts">Buscar</v-btn>
          </v-col>
          <v-spacer></v-spacer>
          <v-col cols="auto">
            <v-btn fab small color="secondary" @click="openCreate">
              <v-icon>mdi-plus</v-icon>
            </v-btn>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <v-card>
      <v-data-table
        :headers="headers"
        :items="products"
        hide-default-footer
        :loading="$store.state.retail.isLoading"
        no-data-text="No hay productos"
        :items-per-page="20"
      >
        <template v-slot:[`item.unit_price`]="{ item }">
          {{ item.unit_price | currency }}
        </template>
        <template v-slot:[`item.stock`]="{ item }">
          <v-chip :color="item.stock > 0 ? 'success' : 'error'" text-color="white" small>
            {{ item.stock }}
          </v-chip>
        </template>
        <template v-slot:[`item.office`]="{ item }">
          {{ item.office ? item.office.name : '—' }}
        </template>
        <template v-slot:[`item.actions`]="{ item }">
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-icon color="blue" v-bind="attrs" v-on="on" class="px-1" @click="openMovements(item)">
                mdi-chart-timeline-variant
              </v-icon>
            </template>
            <span>KARDEX</span>
          </v-tooltip>
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-icon color="success" v-bind="attrs" v-on="on" class="px-1" @click="openStock(item)">
                mdi-package-up
              </v-icon>
            </template>
            <span>AJUSTAR STOCK</span>
          </v-tooltip>
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-icon color="primary" v-bind="attrs" v-on="on" class="px-1" @click="openEdit(item)">
                mdi-pencil
              </v-icon>
            </template>
            <span>EDITAR</span>
          </v-tooltip>
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-icon color="red" v-bind="attrs" v-on="on" class="px-1" @click="confirmDelete(item)">
                mdi-delete
              </v-icon>
            </template>
            <span>ELIMINAR</span>
          </v-tooltip>
        </template>
      </v-data-table>
      <v-pagination
        v-model="page"
        :length="totalPages"
        :total-visible="8"
        circle
        class="mt-2"
        @input="loadProducts"
      ></v-pagination>
    </v-card>

    <!-- Create / Edit dialog -->
    <v-dialog v-model="dialog" width="500" persistent>
      <v-card>
        <v-card-title>{{ editing ? 'Editar' : 'Crear' }} producto de caja</v-card-title>
        <v-divider></v-divider>
        <v-card-text class="pt-4">
          <v-form ref="form" v-model="valid" lazy-validation>
            <v-text-field
              v-model="model.code"
              :rules="[r.required]"
              label="Código"
              outlined dense
              counter="15"
              maxlength="15"
            ></v-text-field>
            <v-text-field
              v-model="model.name"
              :rules="[r.required]"
              label="Nombre"
              outlined dense
            ></v-text-field>
            <v-textarea
              v-model="model.description"
              label="Descripción (opcional)"
              rows="2"
              outlined dense
            ></v-textarea>
            <v-text-field
              v-model="model.unit_price"
              :rules="[r.required, r.positive]"
              label="Precio unitario"
              type="number"
              outlined dense
            ></v-text-field>
            <v-text-field
              v-if="!editing"
              v-model="model.initial_stock"
              label="Stock inicial"
              type="number"
              outlined dense
              :rules="[r.nonNegative]"
            ></v-text-field>
            <v-autocomplete
              v-if="role === 'SUPERADMIN'"
              v-model="model.office_id"
              :items="offices"
              item-text="name"
              item-value="id"
              label="Sede"
              :rules="[r.required]"
              outlined dense
            ></v-autocomplete>
          </v-form>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text color="error" @click="dialog = false">Cancelar</v-btn>
          <v-btn text color="success" :disabled="!valid" @click="save">Guardar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Stock adjustment dialog -->
    <v-dialog v-model="dialogStock" width="420" persistent>
      <v-card>
        <v-card-title>Ajustar stock — {{ selectedProduct ? selectedProduct.name : '' }}</v-card-title>
        <v-divider></v-divider>
        <v-card-text class="pt-4">
          <v-form ref="formStock" v-model="validStock" lazy-validation>
            <v-select
              v-model="stockModel.type"
              :items="stockTypes"
              item-text="text"
              item-value="value"
              label="Tipo de movimiento"
              :rules="[r.required]"
              outlined dense
            ></v-select>
            <v-text-field
              v-model="stockModel.quantity"
              :rules="[r.required, r.positive]"
              :label="stockModel.type === 'ADJUST' ? 'Nuevo stock total' : 'Cantidad a ingresar'"
              type="number"
              outlined dense
            ></v-text-field>
            <v-text-field
              v-model="stockModel.notes"
              label="Observaciones (opcional)"
              outlined dense
            ></v-text-field>
          </v-form>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text color="error" @click="dialogStock = false">Cancelar</v-btn>
          <v-btn text color="success" :disabled="!validStock" @click="saveStock">Guardar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Kardex dialog -->
    <v-dialog v-model="dialogMovements" width="700" persistent>
      <v-card>
        <v-card-title>Kardex — {{ selectedProduct ? selectedProduct.name : '' }}</v-card-title>
        <v-divider></v-divider>
        <v-card-text class="pa-0">
          <v-data-table
            :headers="movHeaders"
            :items="$store.state.retail.movements"
            hide-default-footer
            no-data-text="Sin movimientos"
            :items-per-page="50"
          >
            <template v-slot:[`item.type`]="{ item }">
              <v-chip :color="movColor(item.type)" text-color="white" small>{{ movLabel(item.type) }}</v-chip>
            </template>
            <template v-slot:[`item.user`]="{ item }">{{ item.user ? item.user.name : '—' }}</template>
            <template v-slot:[`item.created_at`]="{ item }">{{ item.created_at | shortDate }}</template>
          </v-data-table>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text color="error" @click="dialogMovements = false">Cerrar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Delete confirm -->
    <v-dialog v-model="dialogDelete" width="450" persistent>
      <v-card>
        <v-card-title>Eliminar producto</v-card-title>
        <v-card-text v-if="selectedProduct">
          ¿Eliminar <b>{{ selectedProduct.name }}</b>? Esta acción no se puede deshacer.
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="dialogDelete = false">Cancelar</v-btn>
          <v-btn text color="error" @click="doDelete">Eliminar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { SET_OFFICES } from "@/store/offices";
export default {
  name: "RetailProducts",
  filters: {
    currency(v) {
      return new Intl.NumberFormat("es-CO", { style: "currency", currency: "COP" }).format(v);
    },
    shortDate(v) {
      return v ? v.substring(0, 16).replace("T", " ") : "";
    },
  },
  data: () => ({
    search: "",
    filterOffice: null,
    page: 1,
    totalPages: 1,
    products: [],
    offices: [],
    dialog: false,
    dialogStock: false,
    dialogMovements: false,
    dialogDelete: false,
    editing: false,
    selectedProduct: null,
    valid: true,
    validStock: true,
    model: { code: "", name: "", description: "", unit_price: "", initial_stock: 0, office_id: "" },
    stockModel: { type: "IN", quantity: "", notes: "" },
    stockTypes: [
      { value: "IN", text: "Entrada (aumentar stock)" },
      { value: "ADJUST", text: "Ajuste (fijar stock total)" },
    ],
    headers: [
      { text: "Código", value: "code" },
      { text: "Nombre", value: "name", filterable: true },
      { text: "Descripción", value: "description", sortable: false },
      { text: "Precio", value: "unit_price" },
      { text: "Stock", value: "stock" },
      { text: "Sede", value: "office", sortable: false },
      { text: "Acciones", value: "actions", sortable: false },
    ],
    movHeaders: [
      { text: "Tipo", value: "type" },
      { text: "Cantidad", value: "quantity" },
      { text: "Stock anterior", value: "previous_stock" },
      { text: "Stock nuevo", value: "new_stock" },
      { text: "Notas", value: "notes", sortable: false },
      { text: "Usuario", value: "user", sortable: false },
      { text: "Fecha", value: "created_at" },
    ],
    r: {
      required: (v) => !!v || "Campo requerido",
      positive: (v) => parseFloat(v) > 0 || "Debe ser mayor a 0",
      nonNegative: (v) => parseFloat(v) >= 0 || "No puede ser negativo",
    },
  }),
  computed: {
    role() { return this.$store.state.user.user?.role; },
  },
  created() {
    this.loadProducts();
    this.model.office_id = this.$store.state.user.user.office_id;
    if (this.role === 'SUPERADMIN') {
      this.$store.dispatch("getOfficesAll");
      this.$store.subscribe((m, s) => {
        if (m.type === SET_OFFICES) this.offices = s.offices.offices;
      });
    }
  },
  methods: {
    loadProducts() {
      let q = "page=" + this.page;
      if (this.search) q += "&search=" + this.search;
      if (this.filterOffice) q += "&office=" + this.filterOffice;
      this.$store.dispatch("getRetailProducts", q + "&").then(() => {
        const data = this.$store.state.retail.products;
        this.products = data.data || data;
        this.totalPages = data.last_page || 1;
      });
    },
    openCreate() {
      this.editing = false;
      this.selectedProduct = null;
      this.model = { code: "", name: "", description: "", unit_price: "", initial_stock: 0, office_id: this.$store.state.user.user.office_id };
      this.dialog = true;
    },
    openEdit(item) {
      this.editing = true;
      this.selectedProduct = item;
      this.model = { code: item.code, name: item.name, description: item.description, unit_price: item.unit_price, office_id: item.office_id };
      this.dialog = true;
    },
    save() {
      if (!this.$refs.form.validate()) return;
      if (this.editing) {
        this.$store.dispatch("updateRetailProduct", { form: this.model, id: this.selectedProduct.id }).then(() => {
          this.dialog = false;
          this.loadProducts();
        });
      } else {
        this.$store.dispatch("createRetailProduct", this.model).then(() => {
          this.dialog = false;
          this.loadProducts();
        });
      }
    },
    openStock(item) {
      this.selectedProduct = item;
      this.stockModel = { type: "IN", quantity: "", notes: "" };
      this.dialogStock = true;
    },
    saveStock() {
      if (!this.$refs.formStock.validate()) return;
      this.$store.dispatch("adjustRetailStock", { form: this.stockModel, id: this.selectedProduct.id }).then(() => {
        this.dialogStock = false;
        this.loadProducts();
      });
    },
    openMovements(item) {
      this.selectedProduct = item;
      this.$store.dispatch("getRetailMovements", item.id);
      this.dialogMovements = true;
    },
    confirmDelete(item) {
      this.selectedProduct = item;
      this.dialogDelete = true;
    },
    doDelete() {
      this.$store.dispatch("deleteRetailProduct", this.selectedProduct.id).then(() => {
        this.dialogDelete = false;
        this.loadProducts();
      });
    },
    movColor(type) { return type === "IN" ? "success" : type === "OUT" ? "error" : "blue"; },
    movLabel(type) { return type === "IN" ? "ENTRADA" : type === "OUT" ? "SALIDA" : "AJUSTE"; },
  },
};
</script>
